import { createTheme } from '@mui/material/styles';
import { clarityTokens, generateColor, generateOpacityColor } from '@verifime/design-tokens';
import {
  commonComponentsOverride,
  identityCard,
  paletteGrey,
  requestActions,
  requestBanner,
  requestCard,
  requestDialog,
  semanticBorderRadius,
  semanticFontWeight,
  semanticFontSize,
  semanticLetterSpacing,
  semanticLineHeight,
  semanticSpacing,
  semanticSize,
  typographyTheme,
} from './common';

const theme = createTheme({
  semantic: {
    borderRadius: semanticBorderRadius,
    fontWeight: semanticFontWeight,
    fontSize: semanticFontSize,
    letterSpacing: semanticLetterSpacing,
    lineHeight: semanticLineHeight,
    spacing: semanticSpacing,
    size: semanticSize,
  },
  logo: {
    width: semanticSize['2xs'],
    padding: semanticSpacing['2xs'],
    borderRadius: semanticSpacing['2xs'],
  },
  card: {
    padding: semanticSpacing.base,
    borderRadius: semanticBorderRadius.sm,
    borderWidth: semanticSpacing['4xs'],
    contentGap: semanticSpacing.sm,
    itemGap: semanticSpacing.sm,
  },
  // Hover and press colours of the request-screen identity cards (the DS card.background.hover,
  // card.border.hover and card.background.active tones).
  identityCard: {
    ...identityCard,
    hoverBackground: clarityTokens.colorEggplant900,
    hoverBorder: clarityTokens.colorEggplant800,
    activeBackground: clarityTokens.colorEggplant800,
  },
  requestActions,
  // The board's default border colour for a dot, and the tint behind a back or next button under
  // the pointer (the DS subtle active primary tone).
  requestBanner: {
    ...requestBanner,
    dotColor: clarityTokens.colorGrey700,
    stepHoverBackground: clarityTokens.colorEggplant800,
  },
  requestCard,
  requestDialog,
  notice: {
    info: {
      background: clarityTokens.colorBlue900,
      foreground: clarityTokens.colorBlue300,
    },
    danger: {
      background: clarityTokens.colorRed900,
      foreground: clarityTokens.colorRed300,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: generateColor(clarityTokens.brandColorPrimary, '300', 'clarity'),
      dark: generateColor(clarityTokens.brandColorPrimary, '500', 'clarity'),
      light: generateColor(clarityTokens.brandColorPrimary, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    secondary: {
      main: generateColor(clarityTokens.brandColorSecondary, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorSecondary, '300', 'clarity'),
      light: generateColor(clarityTokens.brandColorSecondary, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    error: {
      main: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorError, '300', 'clarity'),
      light: generateColor(clarityTokens.brandColorError, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    warning: {
      main: generateColor(clarityTokens.brandColorWarning, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorWarning, '300', 'clarity'),
      light: generateColor(clarityTokens.brandColorWarning, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    info: {
      main: generateColor(clarityTokens.brandColorInfo, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorInfo, '300', 'clarity'),
      light: generateColor(clarityTokens.brandColorInfo, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    success: {
      main: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
      dark: generateColor(clarityTokens.brandColorSuccess, '300', 'clarity'),
      light: generateColor(clarityTokens.brandColorSuccess, '600', 'clarity'),
      contrastText: clarityTokens.colorWhite,
    },
    text: {
      primary: clarityTokens.colorWhite,
      secondary: clarityTokens.colorWhite,
      disabled: clarityTokens.colorGrey500,
      permanentBlack: clarityTokens.colorBlack,
      permanentWhite: clarityTokens.colorWhite,
      tertiary: clarityTokens.colorGrey300,
      // Three steps lighter than the clarity light theme's, for the same contrast reason the
      // default dark theme gives: clarity's paper is the same Grey 800.
      success: generateColor(clarityTokens.brandColorSuccess, '300', 'clarity'),
      danger: generateColor(clarityTokens.brandColorError, '300', 'clarity'),
    },
    // Holds Eggplant 500 in both themes, the same numbered gradation repeated rather than a
    // dedicated ramp: see risk.high.fill below for the precedent, and Palette.permanent for why.
    permanent: {
      primary: generateColor(clarityTokens.brandColorPrimary, '500', 'clarity'),
      // Same reasoning as primary above: StatusKnockoutTile's failure states must read as the
      // same red regardless of light/dark mode (C4), so this repeats Red 500 in both theme
      // files rather than tracking mode the way palette.error.main does.
      danger: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
    },
    background: {
      default: clarityTokens.colorGrey900,
      paper: clarityTokens.colorGrey800,
    },
    action: {
      disabled: clarityTokens.colorGrey500,
      disabledBackground: clarityTokens.colorGrey100,
    },
    surface: {
      logo: clarityTokens.colorWhite,
      brandSubtle: generateColor(clarityTokens.brandColorPrimary, '900', 'clarity'),
      successSubtle: generateColor(clarityTokens.brandColorSuccess, '900', 'clarity'),
      dangerSubtle: generateColor(clarityTokens.brandColorError, '900', 'clarity'),
      warningSubtle: generateColor(clarityTokens.brandColorWarning, '900', 'clarity'),
      neutralSubtle: clarityTokens.colorGrey900,
      // One step lighter than neutralSubtle: the artboard's interactive neutral surface for the
      // expiry and attribution panels in dark mode. Grey 800 happens to hold this exact value,
      // but it is named here as an intent (a raised neutral surface) rather than reached for as
      // a primitive by coincidence.
      neutralSubtleRaised: clarityTokens.colorGrey800,
    },
    border: {
      decorative: clarityTokens.colorGrey400,
      // Named subtleAccent, not a hue-specific name, because this does not hold a brand tint
      // across modes the way the other *Subtle tokens do: measured directly off the artboard's
      // dark requirement card (see PR 2140's report), this resolves to a neutral grey rather
      // than a darker step of the Eggplant ramp.
      subtleAccent: clarityTokens.colorGrey700,
    },
    status: {
      success: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      warning: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorWarning, '300', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      error: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      info: {
        text: generateColor(clarityTokens.brandColorInfo, '300', 'clarity'),
        border: generateColor(clarityTokens.brandColorInfo, '300', 'clarity'),
      },
    },
    interactive: {
      disabled: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey500,
        icon: clarityTokens.colorWhite,
      },
      enabled: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorWhite,
        icon: generateOpacityColor(clarityTokens.colorWhite, 0.5),
      },
    },
    risk: {
      extreme: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey800,
        border: clarityTokens.colorWhite,
        icon: clarityTokens.colorWhite,
      },
      high: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      medium: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorWarning, '300', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      low: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      pending: {
        text: clarityTokens.colorGrey500,
        fill: clarityTokens.colorGrey800,
        border: clarityTokens.colorGrey500,
        icon: generateOpacityColor(clarityTokens.colorWhite, 0.5),
      },
    },
    confidence: {
      none: {
        text: clarityTokens.colorGrey300,
        fill: clarityTokens.colorGrey800,
      },
      pending: {
        text: clarityTokens.colorGrey300,
        fill: clarityTokens.colorGrey600,
      },
      low: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
      },
      substantial: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorWarning, '300', 'clarity'),
      },
      high: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
      },
      /**
       * The "what do you do now" box beneath a card's statement. The light theme pairs `'050'`
       * fill with `'800'` text; here that pair is inverted to `'900'` fill with `'100'` text, so
       * the pale tint and dark text of the light theme become a dark tint and pale text against
       * dark paper. That is the same direction `confidence.none` takes with its greys just above
       * (`colorGrey100` in light, `colorGrey800` in dark).
       */
      guidance: {
        fill: generateColor(clarityTokens.brandColorPrimary, '900', 'clarity'),
        text: generateColor(clarityTokens.brandColorPrimary, '100', 'clarity'),
      },
    },
    // Identical across all four themes on purpose: these sit over a video frame, not over
    // the page surface, so they take their contrast from the capture rather than the theme.
    mediaOverlay: {
      scrim: generateOpacityColor(clarityTokens.colorBlack, 0.72),
      control: generateOpacityColor(clarityTokens.colorWhite, 0.22),
      controlHover: generateOpacityColor(clarityTokens.colorWhite, 0.34),
      track: generateOpacityColor(clarityTokens.colorWhite, 0.28),
      halo: generateOpacityColor(clarityTokens.colorWhite, 0.34),
    },
    entity: {
      verified: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorSuccess, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      rejected: {
        text: clarityTokens.colorWhite,
        fill: generateColor(clarityTokens.brandColorError, '500', 'clarity'),
        icon: clarityTokens.colorWhite,
      },
      draft: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey500,
        icon: generateOpacityColor(clarityTokens.colorWhite, 0.5),
      },
      inProgress: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey500,
        icon: clarityTokens.colorWhite,
      },
      empty: {
        text: clarityTokens.colorWhite,
        fill: clarityTokens.colorGrey500,
        icon: clarityTokens.colorWhite,
      },
    },
    grey: paletteGrey,
  },
  typography: typographyTheme,
  components: {
    ...commonComponentsOverride,
    MuiButton: {
      ...commonComponentsOverride.MuiButton,
      variants: [
        ...(commonComponentsOverride.MuiButton?.variants || []),
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: generateColor(clarityTokens.brandColorPrimary, '500', 'clarity'),
            color: clarityTokens.colorWhite,
            '&:hover': {
              backgroundColor: generateColor(clarityTokens.brandColorPrimary, '600', 'clarity'),
            },
          },
        },
      ],
    },
    MuiLink: {
      ...commonComponentsOverride.MuiLink,
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const color = ownerState.color;

          if (!color) {
            return {
              '&:hover': {
                color: generateColor(clarityTokens.brandColorPrimary, '200', 'clarity'),
              },
            };
          }

          // Handle dot notation colors like 'warning.main'
          if (typeof color === 'string' && color.includes('.')) {
            const [paletteColor] = color.split('.');
            const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];

            if (validColors.includes(paletteColor)) {
              return {
                '&:hover': {
                  color:
                    theme.palette[
                      paletteColor as
                        | 'primary'
                        | 'secondary'
                        | 'error'
                        | 'warning'
                        | 'info'
                        | 'success'
                    ].dark,
                },
              };
            }
          }

          // Handle simple color names
          const validColors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'];
          if (validColors.includes(color as string)) {
            return {
              '&:hover': {
                color:
                  theme.palette[
                    color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
                  ].dark,
              },
            };
          }

          return {
            '&:hover': {
              color: generateColor(clarityTokens.brandColorPrimary, '200', 'clarity'),
            },
          };
        },
      },
    },
  },
});

export default theme;
